/**
 * 네비게이션 그룹 정의 (HomeNavBar / AppHeader 모바일 Drawer 공통)
 *
 * HomeNavBar(데스크톱 메가 메뉴)와 AppHeader의 모바일 Drawer가
 * 동일한 메뉴 구성/권한 필터링 규칙을 공유하도록 단일 소스로 추출한다.
 *
 * 구조:
 *   - 그룹(컬럼): prd / vd / std / doc / admin
 *   - 일반 아이템: { key: '/경로', label: '...' }
 *   - 외부 링크: { key: 'https://...', label: '...', external: true }
 *   - accordion 부모: { key: 'xxx', label: '...', children: [...] }
 *
 * 권한 필터링 규칙 (HomeNavBar의 filteredNavGroups 로직과 동일):
 *   1. 일반 아이템: 라우트 권한 없으면 제외 (external은 아래 3번 규칙)
 *   2. accordion 부모: 접근 가능한 자식이 하나도 없으면 제외
 *   3. external 링크: 그룹 내 다른 가시(non-external) 아이템이 있을 때만 표시
 *   4. 그룹: 남은 non-external 아이템이 없으면 그룹 자체 제거
 */
import { getRoutePermission } from './routePermissions';
import { canAccessRoute } from '../components/common/PermissionGuard';

/**
 * 다국어 레이블 객체(t)를 받아 NAV_GROUPS 구조를 생성한다.
 *
 * 호출자는 `useLanguage(TEXTS)`로 얻은 t를 그대로 전달하면 된다.
 * TEXTS에 필요한 키가 모두 정의되어 있어야 한다 (ko/en 공통).
 *
 * @param {Object} t - 다국어 레이블 객체
 * @returns {Array} NAV_GROUPS — 원본(필터링 전) 구조
 */
export function buildNavGroups(t) {
  return [
    {
      key: 'prd',
      label: t.prd,
      items: [
        { key: '/prd/pesticides', label: t.prdInfo },
        { key: '/prd/mrls', label: t.mrl },
        { key: '/prd/progress', label: t.progress },
        { key: 'https://psis.rda.go.kr/psis/', label: t.prdUsageInfo, external: true },
      ],
    },
    {
      key: 'vd',
      label: t.vd,
      items: [
        { key: '/vd/drugs', label: t.vdInfo },
        { key: '/vd/mrls', label: t.mrl },
        { key: 'https://medi.qia.go.kr/searchMedicine', label: t.vdUsageInfo, external: true },
      ],
    },
    {
      key: 'std',
      label: t.std,
      items: [
        { key: '/std/standards', label: t.standards },
        { key: '/std/applications', label: t.applications },
        // { key: '/std/requests', label: t.requests },  // 의도적 주석 — HomeNavBar 원본과 동일
      ],
    },
    {
      key: 'doc',
      label: t.doc,
      items: [
        { key: '/doc/prd-board', label: t.prdBoard },
        { key: '/doc/vd-board', label: t.vdBoard },
      ],
    },
    {
      key: 'admin',
      label: t.admin,
      items: [
        // 대시보드 — direct link (accordion 아님)
        { key: '/admin/dashboard', label: t.dashboard },

        // ── 인증 관리 ──
        {
          key: 'admin-auth',
          label: t.adminAuth,
          children: [
            { key: '/admin/users', label: t.users },
            { key: '/admin/auth-templates', label: t.authTemplates },
          ],
        },

        // ── 기준 정보 관리 ──
        {
          key: 'admin-base',
          label: t.adminBase,
          children: [
            { key: '/admin/master/companies', label: t.masterCompanies },
            { key: '/admin/master/units', label: t.masterUnits },
            { key: '/admin/master/statuses', label: t.masterStatuses },
            { key: '/admin/master/countries', label: t.masterCountries },
            { key: '/admin/master/provinces', label: t.masterProvinces },
          ],
        },

        // ── 농약 식품 관리 ──
        {
          key: 'admin-prd-food',
          label: t.adminPrdFood,
          children: [
            { key: '/admin/master/prd-class-large', label: t.foodClassLarge },
            { key: '/admin/master/prd-class-middle', label: t.foodClassMiddle },
            { key: '/admin/master/prd-class-small', label: t.foodClassSmall },
            { key: '/admin/master/prd-foods', label: t.foodMaster },
          ],
        },

        // ── 동약 식품 관리 ──
        {
          key: 'admin-vd-food',
          label: t.adminVdFood,
          children: [
            { key: '/admin/master/vd-class-large', label: t.foodClassLarge },
            { key: '/admin/master/vd-class-middle', label: t.foodClassMiddle },
            { key: '/admin/master/vd-class-small', label: t.foodClassSmall },
            { key: '/admin/master/vd-foods', label: t.foodMaster },
          ],
        },

        // ── 시스템 관리 ──
        {
          key: 'admin-system',
          label: t.adminSystem,
          children: [
            { key: '/admin/audit', label: t.audit },
            { key: '/admin/workflow', label: t.workflow },
            { key: '/admin/batch', label: t.batch },
            { key: '/admin/quality', label: t.quality },
            { key: '/admin/visitors', label: t.visitors },
            { key: '/admin/notifications', label: t.notifications },
            { key: '/admin/popups', label: t.popups },
            { key: '/admin/master/system-maintenance', label: t.systemMaintenance },
          ],
        },
      ],
    },
  ];
}

/**
 * NAV_GROUPS를 권한 기준으로 필터링한다 (순수 함수).
 *
 * 규칙:
 *   1. 일반 아이템: getRoutePermission(item.key)에 접근 가능하면 포함
 *   2. external 링크: "그룹에 다른 가시 아이템이 있을 때만" 표시 (단독 표시는 안 됨)
 *   3. accordion 부모: 접근 가능한 자식(external 제외)이 하나도 없으면 제외
 *   4. 그룹: 접근 가능한 non-external 아이템이 하나도 없으면 그룹 자체 제거
 *
 * @param {Array} groups - buildNavGroups(t)의 반환값
 * @param {Object} user - useAuth().user
 * @param {Function} hasPermission - useAuth().hasPermission
 * @returns {Array} 권한 기반 필터링된 NAV_GROUPS
 */
export function filterNavGroups(groups, user, hasPermission) {
  return groups
    .map((group) => {
      const accessibleItems = group.items
        .map((item) => {
          // accordion 부모: 접근 가능한 자식만 남김
          if (item.children) {
            const visibleChildren = item.children.filter((child) => {
              if (child.external) return true; // external 자식은 가시성 판단에 영향 없음 (부모가 보이면 같이 표시)
              const required = getRoutePermission(child.key);
              return canAccessRoute(user, hasPermission, required);
            });
            // 접근 가능한 일반 자식이 하나도 없으면 accordion 자체 제거
            const nonExternalChildren = visibleChildren.filter((c) => !c.external);
            if (nonExternalChildren.length === 0) return null;
            return { ...item, children: visibleChildren };
          }

          // external 링크: 이 단계에서는 통과, 그룹 단위에서 "단독 표시 방지" 처리
          if (item.external) return item;

          // 일반 아이템: 라우트 권한 체크
          const required = getRoutePermission(item.key);
          return canAccessRoute(user, hasPermission, required) ? item : null;
        })
        .filter(Boolean);

      // 그룹 내 non-external 가시 아이템이 하나도 없으면 그룹 전체 제거
      // (external은 혼자 남을 수 없음)
      const nonExternal = accessibleItems.filter((i) => !i.external);
      if (nonExternal.length === 0) return null;

      return { ...group, items: accessibleItems };
    })
    .filter(Boolean);
}
